import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { StoreMixin, store } from '../store.js';
import {
  TYPE_SLOTS,
  OTHER_SLOT,
  STATUS_STATES,
  CONFIDENCE_TIERS,
  RECORD_STATUSES,
} from '../lib/palette.js';
import { swatchSvg, fillFor, fillForTier } from '../lib/patterns.js';
import { stack } from '../lib/scales.js';
import { hours } from '../lib/format.js';

/**
 * The visual review surface for Phase 19: every encoding this app is allowed to use, on one
 * page, in whichever theme is selected. It stands in for a component explorer — the same
 * components the views will use, rendered against sample data, so the gate can check the
 * palette, the patterns, the status colours and the chart chrome in light and dark and with a
 * colour-vision-deficiency simulator without waiting for a chart to exist.
 *
 * Sample data only. Nothing here reads the forecast.
 */
const WEEKS = ['Sep 14', 'Sep 21', 'Sep 28', 'Oct 5', 'Oct 12', 'Oct 19', 'Oct 26', 'Nov 2'];
const LAYERS = [
  {
    label: 'Non-project',
    token: '--type-other',
    fill: 'solid',
    values: [120, 118, 122, 119, 121, 120, 118, 120],
  },
  {
    label: 'Recurring',
    token: '--type-3',
    fill: 'solid',
    values: [64, 64, 63, 64, 64, 63, 64, 64],
  },
  {
    label: 'Committed (explicit)',
    token: '--type-1',
    fill: 'solid',
    values: [210, 232, 198, 176, 154, 132, 96, 74],
  },
  {
    label: 'Committed (inferred)',
    token: '--type-4',
    fill: 'hatch',
    values: [38, 44, 52, 61, 68, 72, 78, 80],
  },
  {
    label: 'Pipeline (weighted)',
    token: '--type-2',
    fill: 'dots',
    values: [12, 18, 34, 52, 74, 96, 118, 134],
  },
];
const CAPACITY = 470;

const SAMPLE_ROWS = [
  { week: '2026-09-14', service: 'Design', hours: 132.5, utilization: 1.04, band: 'caution' },
  {
    week: '2026-09-14',
    service: 'Development',
    hours: 188.0,
    utilization: 1.21,
    band: 'overloaded',
  },
  { week: '2026-09-14', service: 'Strategy', hours: 41.25, utilization: 0.62, band: 'underload' },
  { week: '2026-09-21', service: 'Design', hours: 121.0, utilization: 0.95, band: 'healthy' },
  { week: '2026-09-21', service: 'Development', hours: 164.75, utilization: 1.06, band: 'caution' },
  { week: '2026-09-21', service: 'Strategy', hours: 58.5, utilization: 0.88, band: 'healthy' },
];

class PageStyleguide extends StoreMixin(LitElement) {
  static styles = css`
    :host {
      display: block;
    }
    h1 {
      font-size: 28px;
      margin: 0 0 4px;
      color: var(--ink-strong);
    }
    .lede {
      color: var(--ink-muted);
      margin: 0 0 24px;
      max-width: 70ch;
    }
    section {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 22px 24px;
      margin-bottom: 18px;
    }
    h2 {
      font-size: 16px;
      margin: 0 0 4px;
      color: var(--ink-strong);
    }
    .note {
      color: var(--ink-muted);
      font-size: 12.5px;
      margin: 0 0 16px;
      max-width: 80ch;
    }
    .swatches {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    .swatch {
      border: 1px solid var(--border-soft);
      border-radius: 8px;
      padding: 8px 10px;
      min-width: 148px;
    }
    .swatch .chip {
      height: 28px;
      border-radius: 5px;
      margin-bottom: 7px;
    }
    .swatch .name {
      font-size: 12.5px;
      font-weight: 600;
    }
    .swatch .meta {
      font-size: 11.5px;
      color: var(--ink-muted);
      font-family: var(--font-eyebrow);
    }
    .row {
      display: flex;
      flex-wrap: wrap;
      gap: 18px;
      align-items: center;
    }
    .pattern-row {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
    }
    .pattern {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
    }
    .pattern svg {
      display: block;
    }
    .theme-hint {
      font-size: 12.5px;
      color: var(--ink-muted);
      margin-left: auto;
    }
    button.theme {
      font: inherit;
      font-size: 13px;
      padding: 5px 10px;
      border: 1px solid var(--control-border);
      background: var(--control-bg);
      color: var(--ink);
      border-radius: 6px;
      cursor: pointer;
    }
  `;

  _typeSwatches() {
    const assigned = this.appState.palette ?? [];
    const slots = [...TYPE_SLOTS, OTHER_SLOT];
    return slots.map((slot) => {
      const type = assigned.find((t) => t.slot === slot.slot);
      return html`
        <div class="swatch">
          <div class="chip" style=${`background: var(${slot.var})`}></div>
          <div class="name">${type ? type.label : 'Unassigned'}</div>
          <div class="meta">
            slot ${slot.slot} · ${slot.name}${type ? ` · ${type.volume} open` : ''}
          </div>
        </div>
      `;
    });
  }

  _ribbon() {
    const max = Math.max(
      CAPACITY,
      ...WEEKS.map((_, i) => LAYERS.reduce((sum, l) => sum + l.values[i], 0)),
    );
    const marks = ({ x, y, svg: s }) =>
      WEEKS.map((week, i) => {
        const segments = stack(LAYERS.map((layer) => layer.values[i]));
        return segments.map(
          ([from, to], layerIndex) => s`
            <rect x=${x(week)} width=${x.bandwidth()}
                  y=${y(to)} height=${Math.max(0, y(from) - y(to) - 2)}
                  rx="2"
                  fill=${fillFor(LAYERS[layerIndex].token, LAYERS[layerIndex].fill)} />`,
        );
      });

    return html`
      <chart-frame
        .categories=${WEEKS}
        .max=${max}
        .marks=${marks}
        .rule=${CAPACITY}
        rule-label="Available capacity"
        value-label="Hours"
        category-label="Week commencing"
        height="280"
      ></chart-frame>
      <chart-legend
        title="Demand layers"
        .items=${LAYERS.map((l) => ({ label: l.label, token: l.token, fill: l.fill }))}
      ></chart-legend>
    `;
  }

  render() {
    const { theme } = this.appState;
    return html`
      <h1>Style guide</h1>
      <p class="lede">
        Every encoding the views are allowed to use. Switch the theme here and read the same page
        again — the tokens change, the meaning does not.
      </p>

      <section>
        <div class="row">
          <div>
            <h2>Theme</h2>
            <p class="note">
              Dark is a selected palette, not an inversion: each hue has its own dark step,
              validated against the dark surface.
            </p>
          </div>
          <span class="theme-hint">Current: ${theme}</span>
          ${['system', 'light', 'dark'].map(
            (t) => html`<button class="theme" @click=${() => store.setTheme(t)}>${t}</button>`,
          )}
        </div>
      </section>

      <section>
        <h2>Project type palette</h2>
        <p class="note">
          Seven hues plus Other, assigned in a fixed order by open-project volume and never
          re-assigned when a filter removes a type. The assignment comes from the server so every
          view, export and digest agrees.
        </p>
        <div class="swatches">${this._typeSwatches()}</div>
      </section>

      <section>
        <h2>Confidence</h2>
        <p class="note">
          Fill pattern plus a marker, never opacity and never colour alone: high is solid, medium a
          45-degree hatch, low a dot field.
        </p>
        <div class="pattern-row">
          ${CONFIDENCE_TIERS.map(
            (tier) => html`
              <span class="pattern">
                ${unsafeSVG(
                  swatchSvg({ slotVar: '--type-1', fill: fillForTier(tier.key), size: 28 }),
                )}
                <span>${tier.label}${tier.marker ? html` <b>${tier.marker}</b>` : ''}</span>
              </span>
            `,
          )}
        </div>
      </section>

      <section>
        <h2>Record status</h2>
        <p class="note">
          Open work is a solid border, an opportunity a dashed one, and a closed reference project
          is the only thing in the app that is allowed to be faded.
        </p>
        <div class="pattern-row">
          ${RECORD_STATUSES.map(
            (status) => html`
              <span class="pattern" style=${`opacity: ${status.opacity}`}>
                ${unsafeSVG(swatchSvg({ slotVar: '--type-4', border: status.border, size: 28 }))}
                <span>${status.label}</span>
              </span>
            `,
          )}
        </div>
      </section>

      <section>
        <h2>Threshold states</h2>
        <p class="note">
          Reserved colours: never a project type, never a series, and always with an icon and a word
          beside them.
        </p>
        <div class="row">
          ${STATUS_STATES.map(
            (state) => html`<status-badge state=${state.key} value=${'—'}></status-badge>`,
          )}
        </div>
      </section>

      <section>
        <h2>Chart primitives</h2>
        <p class="note">
          One value axis, a recessive grid, a reference rule for capacity, patterned fills for the
          inferred and pipeline layers, and a legend. Sample numbers — no forecast is read here.
        </p>
        ${this._ribbon()}
      </section>

      <section>
        <h2>Table view</h2>
        <p class="note">
          Every chart has one of these, and it is what exports. Sort by any column, page through,
          download the whole set as CSV.
        </p>
        <data-table
          caption="Sample weekly allocation"
          export-name="styleguide-sample"
          page-size="5"
          sort-key="hours"
          sort-direction="desc"
          .columns=${[
            { key: 'week', label: 'Week', type: 'date' },
            { key: 'service', label: 'Service', type: 'text' },
            {
              key: 'hours',
              label: 'Allocated hours',
              type: 'number',
              format: (v) => hours(v, { exact: true }),
            },
            {
              key: 'utilization',
              label: 'Utilization',
              type: 'number',
              format: (v) => `${Math.round(v * 100)}%`,
            },
            {
              key: 'band',
              label: 'State',
              type: 'text',
              format: (v) => html`<status-badge state=${v} compact></status-badge>`,
              csv: (v) => v,
            },
          ]}
          .rows=${SAMPLE_ROWS}
        ></data-table>
      </section>
    `;
  }
}

customElements.define('page-styleguide', PageStyleguide);
